import { mapState, mapGetters } from 'vuex';
import { localizeDate } from '@/utils/date';
import ApiService from '../../services/apiService';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import multiSearchMixin from '@/mixins/multiSearchVirtualDevices';
import healthStatusMixin from '@/components/core/virtualDevice/healthStatusMixin';
export default {
  name: 'ViewVirtualDevicesViewDetails',
  mixins: [healthStatusMixin, multiSearchMixin],
  data: function () {
    return {
      tabItem: 'dashboard',
      viewModel: null,
      deviceTypes: [],
      accounts: [],
      virtualDeviceCount: undefined,
      alarmReportsCount: undefined,
      relatedTemplatesCount: undefined,
      ddTotalCount: undefined,
      firstFetch: true,
      isFetchingView: false
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled', 'language', 'permissions']),
    ...mapGetters({
      tenantId: 'authentication/tenantId',
      isFeatureFlagEnabled: 'app/isFeatureFlagEnabled'
    }),
    smartGroupId() {
      return parseInt(this.$route.params.id, 10);
    },
    headerBG() {
      const imageAttachment = get(this.viewModel, 'meta.viewImageId');
      if (imageAttachment) {
        return `${process.env.VUE_APP_API_HOST}${process.env.VUE_APP_API_PATH}/upload/${imageAttachment}`;
      }
      return null;
    },
    formIsValid() {
      if (this.tabItem === 'info') {
        return (this.$refs['metaEdit'] || {}).valid;
      }
      return false;
    },
    predefinedSearch() {
      return (get(this.viewModel, 'viewConfig.searchItems') || []).map(item => {
        return {
          ...item,
          param: item.variable,
          search: item.search
        };
      });
    },
    tableHeaders() {
      const headers = [{
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.id'),
        value: 'id',
        sortable: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.title'),
        value: 'title',
        sortable: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.deviceDriverId'),
        value: 'deviceDriverInfo',
        sortable: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.account'),
        value: 'Tenant.name',
        sortable: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.createdAt'),
        value: 'createdAt',
        sortable: true
      }, {
        text: this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.tableHeaders.updatedAt'),
        value: 'updatedAt',
        sortable: true
      }, {
        align: 'end',
        sortable: false,
        value: 'actions'
      }];
      if (this.isDeviceTemplateManager) {
        headers.push({
          text: this.$i18n_t('DeviceDriver.list.table.headers.actions'),
          isButtons: true,
          align: 'center',
          sortable: false,
          buttons: [{
            icon: 'fal fa-edit',
            color: this.color,
            onClick: item => {
              this.$router.push(this.getSingleTemplateUrl(item.id));
            }
          }, {
            icon: 'fal fa-trash-alt',
            color: this.color,
            onClick: item => {
              this.onDeleteClick(item);
            }
          }]
        });
      }
      return headers;
    },
    isDeviceTemplateManager() {
      return this.permissions['DeviceTemplate.manage'];
    },
    isVirtualDeviceManager() {
      return this.permissions['VirtualDevice.manage'];
    }
  },
  watch: {
    smartGroupId: {
      handler(newV, oldV) {
        if (newV !== oldV) {
          this.fetchView();
        }
      }
    }
  },
  mounted() {
    switch (this.$route.query.tab) {
      case 'info':
        this.tabItem = 'info';
        break;
      case 'virtualdevices':
        this.tabItem = 'virtualdevices';
        break;
      default:
        this.tabItem = 'dashboard';
        break;
    }
    this.fetchBridges();
    this.fetchAccounts();
    this.fetchView();
  },
  methods: {
    get,
    localizeDate,
    updateDDTotalCount(totalCount) {
      this.ddTotalCount = totalCount;
    },
    openMapview() {
      this.$router.push({
        path: '/virtual-device-groups/map',
        query: {
          view: this.smartGroupId,
          mode: 'devices'
        }
      });
    },
    getVariableText(tag) {
      let tagVariable = tag.variable;
      return this.$i18n_t('DigitalTwin.VirtualDevice.props')[tagVariable] || tagVariable;
    },
    fetchAccounts() {
      return this.$store.dispatch('app/loadAccounts').then(accounts => {
        return this.accounts = accounts;
      });
    },
    accountsLoaded() {
      this.accounts = this.$store.state.app.n2Accounts.accounts;
    },
    findAccountById(id) {
      return this.accounts.find(account => account.id === id);
    },
    fetchBridges() {
      ApiService.getBridgeTypes().then(bridgeTypes => {
        this.bridgeTypes = bridgeTypes.filter(bridge => {
          // this bridge-type must have a create-functionality
          // otherwise it should have a read to get 1 single existing device as import
          if (get(bridge, 'manifest.functionScope.extension.devices.create') || get(bridge, 'manifest.functionScope.extension.devices.read')) {
            return true;
          }
          return false;
        }).map(bridge => bridge.manifest.displayName);
      }).catch(error => {
        console.error(error);
      });
    },
    fetchView() {
      this.alarmReportsCount = undefined;
      this.isFetchingView = true;
      ApiService.virtualDeviceViewGet(this, this.smartGroupId).then(res => {
        const viewModel = {
          ...res,
          account: (this.findAccountById(res.tenantId) || {}).name || '--'
        };
        const searchItems = get(viewModel, 'viewConfig.searchItems', []);
        if (this.isFeatureFlagEnabled('env_dispatcher_devicetype_fallback')) {
          // when devicetype_fallback IS enabled
          // replace deviceDriver with deviceType
          searchItems.forEach(item => {
            if (item.variable === 'deviceDriver') {
              item.variable = 'deviceType';
            }
          });
        } else {
          // when devicetype_fallback IS NOT enabled
          // replace deviceType to deviceDriver
          searchItems.forEach(item => {
            if (item.variable === 'deviceType') {
              item.variable = 'deviceDriver';
            }
          });
        }
        set(viewModel, 'viewConfig.searchItems', searchItems);
        this.viewModel = viewModel;

        // get the count of alarm-report for this group. do not care about sort-order or details
        return ApiService.fetchAlarmLogReports(this, 1, 1, [{
          variable: 'smartGroupId',
          search: this.smartGroupId
        }], {
          variable: 'id',
          direction: 'ASC'
        });
      }).then(response => {
        this.alarmReportsCount = response.pagination.total;
      }).catch(e => {
        console.error('Could not fetch view', e);
      }).finally(() => {
        this.isFetchingView = false;
        this.firstFetch = false;
      });
    },
    saveView() {
      this.$refs['metaEdit'].save();
    },
    updateViewModel(e) {
      this.viewModel = cloneDeep(e);
    },
    createAlarmReport() {
      this.$refs['alarmreportlist'].showCreateDialog = true;
    },
    handleUpdate(options) {
      ApiService.virtualDeviceViewUpdate(this, options).then(() => {
        this.fetchView();
        this.$snack(`${this.$i18n_t('DigitalTwin.VirtualDevice.deviceTemplates.snacks.saveGroupSuccess')}`, 'success');
      }).catch(error => {
        // ApiService.virtualDeviceViewUpdate failed
        console.error(error);
      });
    },
    closeView() {
      this.$router.push({
        path: '/virtual-device-groups'
      });
    },
    async fetchTemplates(options) {
      const {
        search,
        page,
        perPage,
        sortBy,
        sortDirection
      } = options;
      const result = await ApiService.fetchSmartGroupRelatedTemplates({
        page: page,
        pageSize: perPage,
        sortBy: sortBy,
        sortDesc: sortDirection === 'desc',
        search,
        smartGroupId: this.smartGroupId
      });
      (result.data || []).forEach(template => {
        template.deviceDriverInfo = `${template.devicedriver.title} (${template.deviceDriverVersion})`;
      });
      return {
        items: result.data.map(item => {
          let accountName = item.tenantId;
          if (item.account && item.account.name) {
            accountName = item.account.name;
          }
          return {
            ...item,
            account: {
              ...item.account,
              name: accountName
            },
            createdAt: this.localizeDate(item.createdAt),
            updatedAt: this.localizeDate(item.updatedAt)
          };
        }),
        total: result.pagination['total-count']
      };
    },
    getSingleTemplateUrl(id) {
      return `/device-templates/${id}?tab=tab-device-meta`;
    }
  }
};